"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Calendar } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { format } from "date-fns"
import type { DateRange } from "react-day-picker"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

interface DateRangePickerProps {
  dateRange: { from: Date; to: Date } | null
  setDateRange: (range: { from: Date; to: Date } | null) => void
  maxDays: number
  popoverClassName?: string
  showClearButton?: boolean
  portalContainer?: HTMLElement
  align?: "start" | "center" | "end"
}

export function DateRangePicker({
  dateRange,
  setDateRange,
  maxDays,
  popoverClassName,
  showClearButton = false,
  portalContainer,
  align = "end",
}: DateRangePickerProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const selectedRange: DateRange | undefined = dateRange
    ? { from: dateRange.from, to: dateRange.to }
    : undefined

  const handleSelect = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      // Calculate the difference in days
      const diffTime = Math.abs(range.to.getTime() - range.from.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      // Check if range exceeds max allowed days
      if (diffDays > maxDays) {
        toast.error(t("app.ui.dateRange.tooLong", { count: maxDays }), {
          duration: 3000,
        })
        return
      }

      // Both dates selected - complete range
      setDateRange({ from: range.from, to: range.to })
    } else {
      // Incomplete range or cleared - keep it null
      setDateRange(null)
    }
  }

  const setLastWeek = () => {
    const today = new Date()
    const lastWeek = new Date(today)
    lastWeek.setDate(today.getDate() - 7)
    setDateRange({ from: lastWeek, to: today })
    setIsOpen(false)
  }

  const setLastMonth = () => {
    const today = new Date()
    const lastMonth = new Date(today)
    lastMonth.setDate(today.getDate() - 30)
    setDateRange({ from: lastMonth, to: today })
    setIsOpen(false)
  }

  const setLastQuarter = () => {
    const today = new Date()
    const lastQuarter = new Date(today)
    lastQuarter.setDate(today.getDate() - 90)
    setDateRange({ from: lastQuarter, to: today })
    setIsOpen(false)
  }

  const clearSelection = () => {
    setDateRange(null)
    setIsOpen(false)
  }

  const formatDateRange = () => {
    if (!dateRange?.from || !dateRange?.to) return t("app.ui.dateRange.pick")
    const pattern = t("app.ui.dateRange.dateFormat")
    const locale = getDateFnsLocale()
    const from = format(dateRange.from, pattern, { locale })
    const to = format(dateRange.to, pattern, { locale })
    return `${from} - ${to}`
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 bg-transparent">
          <Calendar className="h-4 w-4" />
          {formatDateRange()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={`w-auto p-0 ${popoverClassName ?? ""}`}
        align={align}
        portalContainer={portalContainer}
      >
        <div className="flex justify-between border-b p-3">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={setLastWeek}>
              {t("app.ui.dateRange.lastWeek")}
            </Button>
            <Button variant="outline" size="sm" onClick={setLastMonth}>
              {t("app.ui.dateRange.lastMonth")}
            </Button>
            {maxDays >= 90 && (
              <Button variant="outline" size="sm" onClick={setLastQuarter}>
                {t("app.ui.dateRange.lastQuarter")}
              </Button>
            )}
          </div>
          {showClearButton && dateRange && (
            <Button variant="outline" size="sm" onClick={clearSelection}>
              {t("common.clear")}
            </Button>
          )}
        </div>
        {/* </CHANGE> */}
        <CalendarComponent
          mode="range"
          selected={selectedRange}
          onSelect={handleSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  )
}
